import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { 
  Save, ArrowLeft, Paperclip, History, Search, FileText, 
  Tag, Zap, ChevronLeft, ChevronRight as ChevronRightIcon, 
  Workflow, Plus, Minus, Mail, Award, ShoppingCart, DollarSign, CreditCard, RefreshCw  
} from 'lucide-react';

import UniversalLookup from './UniversalLookup';
import TaxCategoryModal from './TaxCategoryModal';
import StatusModal from './StatusModal';
import StatusHistoryModal from './StatusHistoryModal';
import AttachmentsModal from './AttachmentsModal';
import WorkflowActionModal from './WorkflowActionModal';
import WorkflowHistoryModal from './WorkflowHistoryModal';
import SmartPrintButton from './PrintButton'; 
import { useFontSize } from './FontSizeContext';
import { usePermissions } from './usePermissions';

const RFQDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

  const [activeTab, setActiveTab] = useState('RFQ');
  const [selectedLineIdx, setSelectedLineIdx] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isTaxModalOpen, setIsTaxModalOpen] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [isWFHistoryOpen, setIsWFHistoryOpen] = useState(false);
  const [count, setCount] = useState(0); 
  const [wfContext, setWfContext] = useState({ can_start: false, can_action: false });
  const [nav, setNav] = useState({ first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 });
  const [quotes, setQuotes] = useState([]); // 🎯 مصفوفة عروض الأسعار
  
  const { canCreate, canUpdate } = usePermissions('rfq');
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;

  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const [data, setData] = useState({
    rfqnum: '', description: '', status: 'WAPPR', site_id: session.site_id || '', org_id: session.org_id || '',
    requestedby: session.username || '', totalcost: 0, tax_total: 0, grand_total: 0,
    vendor: '', vendor_email: '', ref_prnum: '', requireddate: '', lines: []
  });

  const [lookup, setLookup] = useState({ isOpen: false, title: '', type: '', field: '', filters: {}, targetIdx: null });
  const isReadOnly = ['APPROVED', 'APPR', 'CLOSE', 'CAN', 'CLOSED'].includes(data.status);

  const loadData = useCallback(async () => {
    if (!isNew) {
      try {
        const res = await axios.get(`/api/rfq/${id}`);
        setData(res.data);
        // 🎯 جلب عروض الأسعار الواردة من الموردين
        const quotesRes = await axios.get(`/api/rfq/${id}/quotes`);
        setQuotes(quotesRes.data || []);
      } catch (err) { toast.error(t('common.load_fail')); }
    }
  }, [id, isNew, t]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (isNew && data.org_id) {
        axios.get(`/api/rfq/next-number/${data.org_id}`).then(res => setData(prev => ({ ...prev, rfqnum: res.data.next_rfqnum })));
    }
  }, [isNew, data.org_id]);

  const loadNavigation = useCallback(async () => {
      if (isNew) return;
      const searchParams = new URLSearchParams(location.search);
      try {
          const res = await axios.get(`/api/rfq/${id}/navigation`, {
              params: {
                  all_sites: searchParams.get('all_sites') || "false", q: searchParams.get('q') || "",
                  status: searchParams.get('status') || "", sortBy: searchParams.get('sortBy') || "rfqnum",
                  sortDir: searchParams.get('sortDir') || "desc"
              },
              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          setNav(res.data);
      } catch (err) { console.error("Nav failed", err); }
  }, [id, isNew, location.search]);

  useEffect(() => { loadNavigation(); }, [loadNavigation]);

  const fetchAndFillPR = async (prnum) => {
    try {
      const loadId = toast.loading(t('rfq.messages.pullingPR', { prnum }));
      const res = await axios.get(`/api/pr/lines/${prnum}`);
      if (res.data && res.data.length > 0) {
        const newLines = res.data.map(l => ({
          itemnum: l.itemnum, description: l.description, qty: l.qty, unitcost: l.unitcost,
          tax_id: l.tax_id, taxrate: l.taxrate, taxamount: l.taxamount, linecost: l.linecost,
          glaccount: l.glaccount, ref_prnum: prnum, ref_prlineid: l.prlineid
        }));
        recalculateTotals([...data.lines, ...newLines]);
        setData(prev => ({ ...prev, ref_prnum: prnum }));
        toast.success(t('rfq.messages.prImported'), { id: loadId });
      }
    } catch (err) { toast.error(t('rfq.messages.prFailed')); }
  };

  const recalculateTotals = (lines) => {
    let subtotal = 0, taxtotal = 0;
    const updatedLines = lines.map(l => {
      const lineBase = (parseFloat(l.qty) || 0) * (parseFloat(l.unitcost) || 0);
      const lineTax = lineBase * ((parseFloat(l.taxrate) || 0) / 100);
      subtotal += lineBase; taxtotal += lineTax;
      return { ...l, taxamount: lineTax, linecost: lineBase + lineTax };
    });
    setData(prev => ({ ...prev, lines: updatedLines, totalcost: subtotal, tax_total: taxtotal, grand_total: subtotal + taxtotal }));
  };

  const handleSendEmail = async () => {
    if (!data.vendor_email) return toast.error(t('rfq.messages.noEmail'));
    const result = await Swal.fire({
      title: t('rfq.swal.sendTitle'), text: t('rfq.swal.sendText', { vendor: data.vendor }),
      icon: 'question', showCancelButton: true, confirmButtonColor: '#1a3353'
    });
    if (result.isConfirmed) {
      try {
        toast.loading(t('rfq.messages.sending'));
        await axios.post(`/api/rfq/send-email/${data.rfqnum}`, {}, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        toast.dismiss(); toast.success(t('rfq.messages.sendSuccess'));
      } catch (err) { toast.dismiss(); toast.error(t('rfq.messages.sendFailed')); }
    }
  };

  // 🎯 دالة الترسية وتوليد أمر الشراء (Award & Generate PO)
  const handleAwardQuote = async (quoteId) => {
      const result = await Swal.fire({
        title: t('rfq.swal.awardTitle'), text: t('rfq.swal.awardText'),
        icon: 'success', showCancelButton: true, confirmButtonColor: '#16a34a'
      });
      if (result.isConfirmed) {
          try {
              toast.loading(t('rfq.messages.awarding'));
              const res = await axios.post(`/api/rfq/award-quote/${quoteId}`, {}, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
              });
              toast.dismiss();
              toast.success(t('rfq.messages.awardSuccess'));
              
              // توجيه لصفحة الـ PO الجديد
              Swal.fire(t('rfq.messages.poCreated'), res.data.po_num, 'success').then(() => {
                  navigate(`/po/${res.data.po_num}`);
              });
          } catch (err) { toast.dismiss(); toast.error(t('rfq.messages.awardFailed')); }
      }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const res = await axios.post('/api/rfq/save', data, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success(t('common.save_success'));
      if (isNew) navigate(`/rfq/${res.data.rfqnum}`);
    } catch (err) { toast.error(t('common.save_fail')); } finally { setLoading(false); }
  };

  const handleSearch = (field, title, type, idx = null) => {
    setLookup({ isOpen: true, title, type, field, filters: { site_id: data.site_id, org_id: data.org_id }, targetIdx: idx });
  };

  const handleLookupSelect = (row) => {
    const val = row.value || row.id || row.prnum || row.itemnum || row.taxid || row.comp_num;
    if (lookup.field === 'ref_prnum') { fetchAndFillPR(val); } 
    else if (lookup.targetIdx !== null) {
      const nl = [...data.lines];
      const idx = lookup.targetIdx;
      if (lookup.field === 'tax_id') { nl[idx].tax_id = row.taxid; nl[idx].taxrate = row.taxrate || 0; } 
      else if (lookup.field === 'itemnum') {
        nl[idx].itemnum = row.itemnum; nl[idx].description = row.description; nl[idx].itemtype = row.record_type;
        nl[idx].tax_id = row.tax_id || nl[idx].tax_id; nl[idx].taxrate = row.taxrate || 0; nl[idx].unitcost = row.avgcost || nl[idx].unitcost;
      }
      else { nl[idx][lookup.field] = val; }
      recalculateTotals(nl);
    } 
    else { setData(prev => ({ ...prev, [lookup.field]: val, ...(lookup.field === 'vendor' ? { vendor_email: row.email } : {}) })); }
    setLookup(p => ({ ...p, isOpen: false }));
  };

  const goTo = (t) => t && navigate(`/rfq/${t}${location.search}`);

  return (
    <div className={`flex flex-col h-screen bg-[#f3f4f6] uppercase tracking-tighter ${isRTL ? 'font-cairo dir-rtl text-right' : 'text-left'}`} style={{ fontSize: `${fontSize}px` }}>
      <UniversalLookup {...lookup} onClose={() => setLookup(p => ({ ...p, isOpen: false }))} onSelect={handleLookupSelect} />
      
      {showStatusModal && <StatusModal currentStatus={data.status} recordId={data.rfqnum} appName="RFQ" onClose={() => setShowStatusModal(false)} onComplete={loadData} />}
      {isTaxModalOpen && <TaxCategoryModal isOpen={isTaxModalOpen} onClose={() => setIsTaxModalOpen(false)} orgId={data.org_id} />}
      {showHistoryModal && <StatusHistoryModal isOpen={showHistoryModal} appName="RFQ" recordId={data.rfqnum} onClose={() => setShowHistoryModal(false)} />}
      <AttachmentsModal isOpen={isDocModalOpen} ownertable="RFQ" ownerid={data.rfqnum} onClose={() => {setIsDocModalOpen(false); loadData();}} />
      {isWFHistoryOpen && <WorkflowHistoryModal module="RFQ" refId={id} onClose={() => setIsWFHistoryOpen(false)} />}

      {/* --- TOOLBAR --- */}
      <div className={`bg-[#1a3353] text-white p-1.5 flex justify-between items-center shadow-lg px-4 shrink-0 `}>
        <div className={`flex items-center gap-3 `}>
          <button onClick={() => navigate('/rfq')} className="p-1.5 hover:bg-white/10 rounded-full"><ArrowLeft size={18} className={isRTL ? 'rotate-180' : ''} /></button>
          {!isNew && (
            <div className={`flex items-center bg-black/20 border border-white/10 rounded px-1 py-0.5 gap-1 font-mono `}>
                <button onClick={() => goTo(nav.first)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronLeft size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
                <button onClick={() => goTo(nav.prev)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
                <span className="px-2 text-orange-400">{nav.current_pos} / {nav.total}</span>
                <button onClick={() => goTo(nav.next)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronRightIcon size={16} className={isRTL ? 'rotate-180' : ''}/></button>
                <button onClick={() => goTo(nav.last)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronRightIcon size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
            </div>
          )}
        </div>

        <div className={`flex items-center gap-2 `}>
          {!isNew && <button onClick={handleSendEmail} className="bg-blue-600 hover:bg-blue-700 px-4 py-1.5 rounded-sm flex items-center gap-2 font-black shadow-md"><Mail size={14}/> {t('rfq.actions.sendEmail')}</button>}
          {((isNew && canCreate) || (!isNew && canUpdate)) && (
            <button onClick={handleSave} disabled={loading || isReadOnly} className="bg-green-600 hover:bg-green-700 px-4 py-1.5 rounded-sm flex items-center gap-2 font-black shadow-md"><Save size={14}/> {loading ? t('common.saving') : t('common.save')}</button>
          )}
          <WorkflowActionModal moduleName="RFQ" refId={id} currentStatus={data.status} onActionComplete={loadData} />
          
          <div className="h-6 w-px bg-white/20 mx-2"></div>
          <button onClick={() => setIsWFHistoryOpen(true)} className="p-2 text-orange-400" title={t('rfq.actions.wfHistory')}><Workflow size={16}/></button>
          <SmartPrintButton moduleName="RFQ" refId={data.rfqnum} />
          <button onClick={() => setShowStatusModal(true)} disabled={isReadOnly} className="p-2 hover:bg-white/10 rounded text-blue-200"><Tag size={16}/></button>
          <button onClick={() => setIsDocModalOpen(true)} className="relative p-2 text-blue-200 hover:bg-white/10 rounded"><Paperclip size={16}/> {count > 0 && <span className="absolute top-1 right-1 bg-red-600 text-[8px] w-3.5 h-3.5 rounded-full flex items-center justify-center font-black">{count}</span>}</button>
          <button onClick={() => setShowHistoryModal(true)} className="p-2 hover:bg-white/10 rounded text-blue-200"><History size={16}/></button>

          <div className={`flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full border border-white/20 ${isRTL ? 'mr-4 flex-row-reverse' : 'ml-4'}`}>
            <button onClick={() => fontContext.changeFontSize(-1)} className="hover:text-orange-400"><Minus size={14} /></button>
            <span className="font-black text-[10px] text-blue-200">{fontSize}</span>
            <button onClick={() => fontContext.changeFontSize(1)} className="hover:text-orange-400"><Plus size={14} /></button>
          </div>
        </div>
      </div>

      {/* --- HEADER --- */}
      <div className={`bg-white border-b px-6 py-3 flex items-center gap-12 shadow-sm shrink-0 `}>
        <HeaderField label={t('rfq.tables.rfqNum')} value={data.rfqnum} isBold width="w-32" disabled isRTL={isRTL} />
        <HeaderField label={t('assets.headers.desc')} value={data.description} onChange={v => setData({...data, description: v})} width="w-[400px]" disabled={isReadOnly} isRTL={isRTL} />
        <HeaderField label={t('assets.headers.status')} value={data.status} disabled width="w-24" isRTL={isRTL} />
        <HeaderField label={t('assets.headers.site')} value={data.site_id} disabled width="w-24" isRTL={isRTL} />
      </div>

      {/* --- TABS --- */}
      <div className={`flex bg-white border-b px-4 shrink-0 font-black uppercase `}>
        {['RFQ', 'RFQ Lines', 'Quotations'].map(tKey => (
          <button key={tKey} onClick={() => setActiveTab(tKey)} className={`px-8 py-3 transition-all tracking-tighter ${activeTab === tKey ? 'border-b-2 border-[#1a3353] text-[#1a3353] bg-gray-50' : 'text-gray-400 hover:text-gray-600'} `}>
            {t(`rfq.tabs.${tKey.replace(' ', '').toLowerCase()}`)}
          </button>
        ))}
      </div>

      {/* --- CONTENT --- */}
      <div className="flex-1 overflow-y-auto p-4 bg-[#f8f9fb] custom-scrollbar">
        {activeTab === 'RFQ' && (
          <div className="grid grid-cols-12 gap-6 max-w-7xl mx-auto pb-20 animate-in fade-in duration-500">
            <div className="col-span-8 space-y-6">
              <SectionCard title={t('rfq.sections.general')} isRTL={isRTL}>
                <div className={`grid grid-cols-2 gap-x-12 gap-y-4 ${isRTL ? 'dir-rtl' : ''}`}>
                  <EditableField label={t('rfq.fields.requestedBy')} value={data.requestedby} disabled={isReadOnly} onChange={v => setData({...data, requestedby: v})} isRTL={isRTL}/>
                  <DateField label={t('rfq.fields.reqDate')} value={data.requireddate} onChange={v => setData({...data, requireddate: v})} isRTL={isRTL}/>
                  <EditableField label={t('rfq.fields.refPr')} value={data.ref_prnum} onSearch={() => handleSearch('ref_prnum', t('common.search'), 'purchaserequests')} disabled={isReadOnly} isRTL={isRTL}/>
                  <EditableField label={t('rfq.fields.vendor')} value={data.vendor} onSearch={() => handleSearch('vendor', t('common.search'), 'companies')} disabled={isReadOnly} isRTL={isRTL}/>
                  <StaticField label={t('rfq.fields.vendorEmail')} value={data.vendor_email} isRTL={isRTL}/>
                </div>
              </SectionCard>
            </div>
            <div className="col-span-4 space-y-6">
              <SectionCard title={t('rfq.sections.financial')} isRTL={isRTL}>
                 <div className={`space-y-4 font-mono ${isRTL ? 'text-right' : 'text-left'}`}>
                    <div className={`flex justify-between border-b pb-1 `}><span className="text-gray-400 uppercase italic text-[9px]">{t('rfq.financial.subtotal')}</span><span className="font-bold">{data.totalcost?.toLocaleString(undefined, {minimumFractionDigits: 2})}</span></div>
                    <div className={`flex justify-between border-b pb-1 text-red-500 `}><span className="uppercase italic text-[9px]">{t('rfq.financial.taxTotal')}</span><span className="font-bold">+{data.tax_total?.toLocaleString(undefined, {minimumFractionDigits: 2})}</span></div>
                    <div className={`flex justify-between bg-blue-50 p-3 rounded border border-blue-100 mt-4 shadow-inner `}>
                        <span className={`font-black text-blue-900 uppercase text-[10px] flex items-center gap-1 `}><CreditCard size={12}/> {t('rfq.financial.grandTotal')}</span>
                        <span className="text-xl font-black text-blue-900">{data.grand_total?.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                    </div>
                 </div>
              </SectionCard>
            </div>
          </div>
        )}

        {activeTab === 'RFQ Lines' && (
          <div className="max-w-7xl mx-auto space-y-4 pb-20 animate-in slide-in-from-bottom duration-500">
            <div className="bg-white border rounded shadow-xl overflow-hidden min-h-[400px]">
              <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
                <thead className="bg-slate-50 border-b text-gray-500 font-black uppercase text-[0.8] tracking-widest">
                  <tr className={isRTL ? 'flex-row-reverse' : ''}>
                    <th className="p-2 w-8"></th>
                    <th className={`p-2 border-x ${isRTL ? 'border-l' : 'border-r'}`}>{t('rfq.tables.itemNum')}</th>
                    <th className={`p-2 border-x ${isRTL ? 'border-l' : 'border-r'}`}>{t('assets.headers.desc')}</th>
                    <th className={`p-2 border-x ${isRTL ? 'border-l' : 'border-r'}`}>{t('rfq.tables.itemType')}</th>
                    <th className={`p-2 border-x text-center ${isRTL ? 'border-l' : 'border-r'}`}>{t('wo.tables.qty')}</th>
                    <th className={`p-2 border-x text-right ${isRTL ? 'border-l text-left' : 'border-r'}`}>{t('rfq.tables.unitCost')}</th>
                    <th className={`p-2 border-x text-center text-red-500 ${isRTL ? 'border-l' : 'border-r'}`}>{t('rfq.tables.tax')} %</th>
                    <th className={`p-2 text-right bg-blue-50/50 ${isRTL ? 'text-left' : ''}`}>{t('common.total')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y font-bold text-[12px]">
                  {(Array.isArray(data.lines) ? data.lines : []).map((l, i) => (
                    <tr key={i} onClick={() => setSelectedLineIdx(i)} className={`hover:bg-blue-50 cursor-pointer ${selectedLineIdx === i ? 'bg-[#e8f0fe]' : ''} `}>
                      <td className="p-2 border-x text-center">{selectedLineIdx === i && <ChevronRightIcon size={12} className={isRTL ? 'rotate-180 mx-auto' : 'mx-auto'}/>}</td>
                      <td className={`p-2 border-x font-black text-blue-900 ${isRTL ? 'border-l' : 'border-r'}`}>{l.itemnum || '---'}</td>
                      <td className={`p-2 border-x text-gray-500 italic ${isRTL ? 'border-l' : 'border-r'}`}>{l.description}</td>
                      <td className={`p-2 border-x text-gray-500 italic ${isRTL ? 'border-l' : 'border-r'}`}>{l.itemtype}</td>
                      <td className={`p-2 border-x text-center font-mono ${isRTL ? 'border-l' : 'border-r'}`}>{l.qty}</td>
                      <td className={`p-2 border-x text-right ${isRTL ? 'border-l text-left' : 'border-r'}`}>{l.unitcost?.toLocaleString()}</td>
                      <td className={`p-2 border-x text-center font-bold text-red-500 ${isRTL ? 'border-l' : 'border-r'}`}>{l.taxrate}%</td>
                      <td className={`p-2 text-right font-black text-green-700 font-mono ${isRTL ? 'text-left' : ''}`}>{parseFloat(l.linecost || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                    </tr>
                  ))}
                  {(!data.lines || data.lines.length === 0) && (
                    <tr><td colSpan="8" className="p-20 text-center text-gray-300 italic font-black uppercase tracking-widest">{t('rfq.messages.noLines')}</td></tr>
                  )}
                </tbody>
              </table>
              <button onClick={() => { const nl = [...(data.lines||[]), {itemnum: '', description: '', qty: 1, unitcost: 0, tax_id: '', taxrate: 0, linecost: 0}]; setData({...data, lines: nl}); setSelectedLineIdx(nl.length-1); }} disabled={isReadOnly} className={`m-4 bg-[#3c70a4] text-white px-6 py-1.5 rounded-sm font-black text-[9px] uppercase hover:bg-black transition-all flex items-center gap-2 active:scale-95 shadow-sm ${isRTL ? 'float-right flex-row-reverse' : ''}`}><Plus size={14}/> {t('common.new_row')}</button>
            </div>
            {selectedLineIdx !== null && data.lines && data.lines[selectedLineIdx] && (
              <div className={`bg-white border p-6 grid grid-cols-5 gap-6 shadow-2xl rounded-sm border-t-4 border-t-orange-600 animate-in zoom-in-95 ${isRTL ? 'dir-rtl' : ''}`}>
                <EditableField label={t('rfq.tables.itemNum')} value={data.lines[selectedLineIdx].itemnum} onSearch={() => handleSearch('itemnum', t('common.search'), 'itemstools', selectedLineIdx)} isRTL={isRTL} />
                <EditableField label={t('common.qty')} type="number" value={data.lines[selectedLineIdx].qty} onChange={v => { const nl = [...data.lines]; nl[selectedLineIdx].qty = v; recalculateTotals(nl); }} isRTL={isRTL} />
                <EditableField label={t('rfq.tables.unitCost')} type="number" value={data.lines[selectedLineIdx].unitcost} onChange={v => { const nl = [...data.lines]; nl[selectedLineIdx].unitcost = v; recalculateTotals(nl); }} isRTL={isRTL} />
                <EditableField label={`${t('rfq.tables.taxCat')} (${data.lines[selectedLineIdx].taxrate || 0}%)`} value={data.lines[selectedLineIdx].tax_id} onSearch={() => handleSearch('tax_id', t('common.search'), 'tax_category', selectedLineIdx)} isRTL={isRTL} />
                <EditableField label={t('rfq.fields.glAccount')} value={data.lines[selectedLineIdx].glaccount} onChange={v => { const nl = [...data.lines]; nl[selectedLineIdx].glaccount = v; setData({...data, lines: nl}); }} isRTL={isRTL} />
              </div>
            )}
          </div>
        )}

        {/* 🎯 TAB 3: Quotations (عروض الأسعار الواردة) */}
        {activeTab === 'Quotations' && (
          <div className="max-w-6xl mx-auto space-y-6 animate-in slide-in-from-right duration-500 pb-20">
            <div className={`flex items-center justify-between bg-blue-50 p-4 border border-blue-100 rounded-sm shadow-inner `}>
              <div className={`flex items-center gap-3 `}>
                <Mail size={24} className="text-blue-500"/>
                <p className={`text-[10px] text-blue-900 italic font-bold leading-tight ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('rfq.messages.quotesHint')}
                </p>
              </div>
              <button onClick={loadData} className="text-blue-600 hover:text-blue-800 transition-colors"><RefreshCw size={16}/></button>
            </div>

            {quotes.map((q, idx) => (
                <div key={idx} className={`bg-white border-2 rounded shadow-xl overflow-hidden mb-6 ${q.is_awarded ? 'border-green-500' : 'border-gray-200 hover:border-blue-400'} transition-all`}>
                    <div className={`p-4 bg-slate-50 border-b flex justify-between items-center `}>
                        <div className={`flex items-center gap-3 `}>
                            <span className="font-black text-[14px] text-[#1a3353]">{q.vendor_id}</span>
                            {q.is_awarded && <span className={`px-3 py-0.5 bg-green-100 text-green-700 text-[9px] font-black rounded-full border border-green-200 flex items-center gap-1 `}><Award size={12}/> {t('rfq.options.awarded')}</span>}
                        </div>
                        <div className={`flex items-center gap-4 `}>
                            <span className="text-[12px] font-black text-gray-500">{new Date(q.received_date).toLocaleDateString()}</span>
                            <span className="text-xl font-black text-green-700">{q.total_amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                            
                            {!q.is_awarded && data.status !== 'CLOSED' && (
                                <button onClick={() => handleAwardQuote(q.quote_id)} className={`bg-green-600 text-white px-6 py-1.5 rounded font-black text-[10px] hover:bg-green-700 transition-all flex items-center gap-2 shadow-md active:scale-95 `}>
                                    <Award size={14}/> {t('rfq.actions.award')}
                                </button>
                            )}
                        </div>
                    </div>
                    <table className={`w-full ${isRTL ? 'text-right' : 'text-left'}`}>
                        <thead className="bg-[#f9fafb] border-b text-gray-400 text-[0.9em] font-black uppercase tracking-widest">
                            <tr className={isRTL ? 'flex-row-reverse' : ''}>
                                <th className={`p-3 border-x ${isRTL ? 'border-l' : 'border-r'}`}>{t('rfq.tables.itemNum')}</th>
                                <th className={`p-3 border-x text-center ${isRTL ? 'border-l' : 'border-r'}`}>{t('wo.tables.qty')}</th>
                                <th className={`p-3 border-x text-right ${isRTL ? 'border-l text-left' : 'border-r text-right'}`}>{t('rfq.tables.unitCost')}</th>
                                <th className={`p-3 text-right bg-green-50/20 text-green-800 ${isRTL ? 'text-left' : 'text-right'}`}>{t('common.total')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y font-bold text-[11px]">
                            {q.lines.map((line, lIdx) => (
                                <tr key={lIdx} className={isRTL ? 'flex-row-reverse' : ''}>
                                    <td className={`p-3 border-x text-blue-900 ${isRTL ? 'border-l' : 'border-r'}`}>{line.itemnum}</td>
                                    <td className={`p-3 border-x text-center font-mono ${isRTL ? 'border-l' : 'border-r'}`}>{line.qty}</td>
                                    <td className={`p-3 border-x font-mono ${isRTL ? 'border-l text-left' : 'border-r text-right'}`}>{line.unit_price?.toLocaleString()}</td>
                                    <td className={`p-3 font-mono text-green-700 bg-green-50/10 ${isRTL ? 'text-left' : 'text-right'}`}>{line.line_total?.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ))}
            {quotes.length === 0 && (
                <div className="p-20 text-center flex flex-col items-center justify-center gap-4 bg-white border border-dashed rounded-lg">
                    <Mail size={40} className="text-gray-300"/>
                    <p className="text-gray-400 italic font-black uppercase tracking-widest text-[11px]">{t('rfq.messages.noQuotes')}</p>
                </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// --- المكونات المساعدة الموحدة ---
const HeaderField = ({ label, value, onChange, width, isBold, disabled, placeholder, isRTL }) => (
    <div className={`flex flex-col px-5 border-gray-100 shrink-0 ${isRTL ? 'border-l last:border-0 text-right' : 'border-r last:border-0 text-left'}`}>
      <label className="text-[0.8em] text-gray-400 font-black uppercase tracking-widest mb-0.5 italic leading-none">{label}</label>
      <input 
          className={`${width} border-b border-transparent outline-none py-0.5 font-black uppercase ${isBold ? 'text-blue-900 text-[13px] tracking-tighter' : 'text-blue-700 text-[12px]'} bg-transparent transition-all focus:border-blue-500 placeholder:opacity-30 ${isRTL ? 'text-right' : 'text-left'}`} 
          value={value || ''} disabled={disabled} onChange={e => onChange && onChange(e.target.value)} placeholder={placeholder}
      />
    </div>
);

const SectionCard = ({ title, children, isRTL }) => (
    <div className="bg-white border-2 border-gray-100 rounded shadow-xl overflow-hidden transition-all hover:shadow-2xl duration-300">
      <div className={`p-2.5 bg-slate-50 border-b-2 border-gray-50 font-black text-slate-500 uppercase text-[1.0em] tracking-widest flex items-center gap-3 italic `}>
        <div className="w-1.5 h-5 bg-orange-600 rounded-full shadow-lg shadow-orange-100"></div> {title}
      </div>
      <div className="p-8">{children}</div>
    </div>
);

const EditableField = ({ label, value, onChange, onSearch, type = "text", disabled, isRTL }) => (
    <div className={`flex flex-col gap-1 border-b-2 border-gray-50 group focus-within:border-[#1a3353] transition-all py-1.5 px-1 ${isRTL ? 'text-right' : 'text-left'}`}>
      <label className="text-gray-400 font-black text-[0.9em] uppercase tracking-widest italic leading-none mb-1 group-hover:text-blue-400 transition-colors">{label}</label>
      <div className={`flex items-center justify-between pb-0.5 h-6 `}>
        <input 
            type={type} 
            className={`outline-none font-black text-blue-900 bg-transparent w-full text-[13px] tracking-tighter uppercase disabled:opacity-50 ${isRTL ? 'text-right' : 'text-left'}`} 
            value={value || ''} 
            disabled={disabled} onChange={e => onChange && onChange(e.target.value)} 
        />
        {onSearch && !disabled && <Search size={16} className="text-blue-300 cursor-pointer hover:text-blue-900 transition-all hover:scale-125 shrink-0" onClick={onSearch} />}
      </div>
    </div>
);

const StaticField = ({ label, value, isRTL }) => (
    <div className={`flex flex-col border-b border-gray-100 py-2.5 px-3 bg-white/40 shadow-inner rounded-sm mb-2 border-blue-900/10 ${isRTL ? 'border-r-4 text-right' : 'border-l-4 text-left'}`}>
        <label className="text-[0.9em] font-black text-gray-400 uppercase tracking-widest italic">{label}</label>
        <div className="font-black uppercase tracking-[0.1em] text-[13px] text-blue-900 italic mt-0.5">{value || '---'}</div>
    </div>
);

const DateField = ({ label, value, onChange, isRTL }) => (
    <div className={`flex flex-col border-b border-gray-300 py-1.5 px-1 group focus-within:border-blue-500 transition-all ${isRTL ? 'text-right' : 'text-left'}`}>
      <label className="text-[0.8em] font-black text-gray-400 uppercase tracking-tighter italic">{label}</label>
      <input 
          type="date" 
          className={`outline-none font-black text-blue-900 bg-transparent py-0.5 text-[12px] ${isRTL ? 'text-right' : 'text-left'}`} 
          value={value ? value.split('T')[0] : ''} 
          onChange={e => onChange(e.target.value)} 
      />
    </div>
);

export default RFQDetails;